import * as React from 'react';
import EmailsGridApiService, {
  SegmentRow,
  SegmentInventoriesSummaryResponse,
  SegmentVehicleItem,
} from '../apiService/emailsGridApiService';

export type VehicleListKind = 'new' | 'used';

export interface ExpandedCell {
  segmentName: string;
  kind: VehicleListKind;
}

/** Headers for grid — not from API. */
const HEADER_ROW = ['Segment', 'New Vehicles', 'Used Vehicles'];

function cacheKey(dealerId: string, segmentName: string, scope: string) {
  return `${dealerId}|${segmentName}|${scope}`;
}

function prefKey(segmentName: string, kind: VehicleListKind) {
  return `${segmentName}-${kind}`;
}

function getResponseErrorMessage(response: any): string | null {
  const statusCode = Number(response?.statusCode);
  const errorCode = Number(response?.errorCode);
  const status = Number(response?.status);
  const hasErrorStatus =
    (Number.isFinite(statusCode) && statusCode >= 400) ||
    (Number.isFinite(errorCode) && errorCode >= 400) ||
    (Number.isFinite(status) && status >= 400);

  if (!hasErrorStatus) return null;

  return (
    response?.myAutoIQ_error_message ||
    response?.message ||
    response?.error ||
    'Request failed'
  );
}

/** Deep compare preferred VINs; returns true if different (order-independent per key). */
function preferredVinsChanged(
  a: Record<string, string[]>,
  b: Record<string, string[]>,
): boolean {
  const keysA = Object.keys(a).sort();
  const keysB = Object.keys(b).sort();
  if (keysA.length !== keysB.length || keysA.some((k, i) => k !== keysB[i])) return true;
  for (const k of keysA) {
    const setA = new Set(a[k] ?? []);
    const setB = new Set(b[k] ?? []);
    if (setA.size !== setB.size || Array.from(setA).some(v => !setB.has(v))) return true;
  }
  return false;
}

export function useSegmentInventories(dealerId: string, modalOpen: boolean) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  /** Preference id from GET response — used for PUT /preferences/:preference_id. */
  const [preferenceId, setPreferenceId] = React.useState<string | null>(null);
  /** Rows from API (properties.rows) — segment_name, new, used. Show exactly what API returns. */
  const [segmentRows, setSegmentRows] = React.useState<SegmentRow[]>([]);

  /**
   * Preferred VINs per segment+kind. Source of truth for "selected" state.
   * Initialized from preferences API; updated when user manually selects/deselects.
   * Use this state for future "update preferences" API.
   * Key = `${segmentName}-${kind}` e.g. "Sedan-new"
   */
  const [preferredVinsByKey, setPreferredVinsByKey] = React.useState<Record<string, string[]>>({});
  /** Snapshot when data was loaded/saved — Save enabled only when current !== initial. */
  const [initialPreferredVinsByKey, setInitialPreferredVinsByKey] = React.useState<
    Record<string, string[]>
  >({});
  /** VINs that are in preferred but not in segment-inventory (stale); shown as cut/missing in UI. Key = prefKey. */
  const [missingVinsByKey, setMissingVinsByKey] = React.useState<Record<string, string[]>>({});

  const [expandedCell, setExpandedCell] = React.useState<ExpandedCell | null>(null);
  const [vehiclesForExpandedCell, setVehiclesForExpandedCell] = React.useState<
    SegmentVehicleItem[]
  >([]);
  const [vehiclesLoading, setVehiclesLoading] = React.useState(false);
  const [vehiclesError, setVehiclesError] = React.useState<string | null>(null);
  const [saveLoading, setSaveLoading] = React.useState(false);

  /**
   * Reactive snapshot of fetched segment inventories. Key format = `${segmentName}-${kind}` (matches `prefKey`).
   * Mirrors `segmentInventoriesCacheRef` but as state so downstream consumers (e.g. Test Template Generation
   * confirmation step) can render full vehicle info, not just VIN strings.
   */
  const [vehiclesBySegmentKind, setVehiclesBySegmentKind] = React.useState<
    Record<string, SegmentVehicleItem[]>
  >({});
  /** True while `prefetchSegmentInventories` is fetching one or more missing segment+kind inventories. */
  const [prefetchLoading, setPrefetchLoading] = React.useState(false);

  /** Cache for segment inventories: key = dealerId|segmentName|scope. Cleared when dealer changes. */
  const segmentInventoriesCacheRef = React.useRef<Map<string, SegmentVehicleItem[]>>(new Map());
  /** Ref to read initial preferred (GET response) when syncing — so missing is computed from server state even when opening from cache. */
  const initialPreferredVinsByKeyRef = React.useRef<Record<string, string[]>>({});

  React.useEffect(() => {
    segmentInventoriesCacheRef.current.clear();
    setVehiclesBySegmentKind({});
  }, [dealerId]);

  /** When dealer changes, reset modal UI state so preferences view is clean for the new dealer. */
  React.useEffect(() => {
    setExpandedCell(null);
    setVehiclesForExpandedCell([]);
    setVehiclesLoading(false);
    setVehiclesError(null);
  }, [dealerId]);

  React.useEffect(() => {
    initialPreferredVinsByKeyRef.current = initialPreferredVinsByKey;
  }, [initialPreferredVinsByKey]);

  /** First API: GET /preferences — call when modal opens; store properties.rows as-is. */
  const fetchSummary = React.useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await EmailsGridApiService.getPreferences(dealerId);
      const responseError = getResponseErrorMessage(response);
      if (responseError) {
        setPreferenceId(null);
        setInitialPreferredVinsByKey({});
        setMissingVinsByKey({});
        setSegmentRows([]);
        setError(responseError);
        return;
      }

      const data = (response?.body ?? response) as
        | SegmentInventoriesSummaryResponse
        | null
        | undefined;
      const rawRows = data?.properties?.rows;
      const rows = Array.isArray(rawRows) ? rawRows : [];
      const id = data?._id ?? data?.id ?? null;
      setPreferenceId(typeof id === 'string' ? id : null);
      setSegmentRows(rows);

      const nextPref: Record<string, string[]> = {};
      for (const row of rows) {
        const name = row?.segment_name;
        if (name) {
          nextPref[prefKey(name, 'new')] = [...(row.new?.vins ?? [])];
          nextPref[prefKey(name, 'used')] = [...(row.used?.vins ?? [])];
        }
      }
      setPreferredVinsByKey(nextPref);
      setInitialPreferredVinsByKey(JSON.parse(JSON.stringify(nextPref)));
      setMissingVinsByKey({});
      setError(null);
    } catch (err: any) {
      setPreferenceId(null);
      setInitialPreferredVinsByKey({});
      setMissingVinsByKey({});
      setSegmentRows([]);
      setError(err?.message || 'Failed to load preferences');
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  React.useEffect(() => {
    if (modalOpen && dealerId) {
      fetchSummary();
    }
  }, [modalOpen, dealerId, fetchSummary]);

  /**
   * Reset transient preferences UI state when the host modal closes so the next open starts clean.
   * Treats close as cancel: unsaved selection edits are discarded by rolling preferred VINs back
   * to the last persisted snapshot. The subsequent open will refetch via `fetchSummary` and
   * overwrite, but this prevents stale UI flashes (e.g. previously expanded cell, dirty selections).
   */
  const prevModalOpenRef = React.useRef(modalOpen);
  React.useEffect(() => {
    const wasOpen = prevModalOpenRef.current;
    prevModalOpenRef.current = modalOpen;
    if (wasOpen && !modalOpen) {
      setExpandedCell(null);
      setVehiclesForExpandedCell([]);
      setVehiclesLoading(false);
      setVehiclesError(null);
      setMissingVinsByKey({});
      setError(null);
      setPreferredVinsByKey(
        JSON.parse(JSON.stringify(initialPreferredVinsByKeyRef.current)),
      );
    }
  }, [modalOpen]);

  /**
   * Compute missing VINs for this segment+kind.
   * Keep missing VINs in preferred state so they render as selected by default
   * and can still be manually deselected from the UI.
   */
  const syncPreferredToInventory = React.useCallback(
    (segmentName: string, kind: VehicleListKind, list: SegmentVehicleItem[]) => {
      const prefKeyForCell = prefKey(segmentName, kind);
      const validVinSet = new Set(
        list.map((v: SegmentVehicleItem) => v.VIN).filter(Boolean),
      );
      const initialForCell = initialPreferredVinsByKeyRef.current[prefKeyForCell] ?? [];
      const missing = initialForCell.filter((vin: string) => !validVinSet.has(vin));
      setMissingVinsByKey(m => ({ ...m, [prefKeyForCell]: missing }));
    },
    [],
  );

  /** On count click: use cache if available, else call getSegmentInventoryBySegmentAndScope. */
  const toggleExpanded = React.useCallback(
    async (segmentName: string, kind: VehicleListKind) => {
      if (expandedCell?.segmentName === segmentName && expandedCell?.kind === kind) {
        setExpandedCell(null);
        setVehiclesForExpandedCell([]);
        setVehiclesError(null);
        return;
      }
      setExpandedCell({ segmentName, kind });
      setVehiclesError(null);
      if (!dealerId) return;

      const scope = kind === 'new' ? 'NEW_VEHICLE' : 'USED_VEHICLE';
      const key = cacheKey(dealerId, segmentName, scope);
      const cached = segmentInventoriesCacheRef.current.get(key);

      if (cached !== undefined) {
        setVehiclesForExpandedCell(cached);
        syncPreferredToInventory(segmentName, kind, cached);
        return;
      }

      setVehiclesLoading(true);
      try {
        const response =
          await EmailsGridApiService.getSegmentInventoryBySegmentAndScope(
            dealerId,
            segmentName,
            scope,
          );
        const responseError = getResponseErrorMessage(response);
        if (responseError) {
          setVehiclesForExpandedCell([]);
          setVehiclesError(responseError);
          return;
        }

        const raw = response?.body ?? response;
        const arr = Array.isArray(raw) ? raw : [];
        const first = arr[0];
        const vehicles = first?.[scope] ?? [];
        const list = Array.isArray(vehicles) ? vehicles : [];
        segmentInventoriesCacheRef.current.set(key, list);
        setVehiclesBySegmentKind(prev => ({ ...prev, [prefKey(segmentName, kind)]: list }));
        setVehiclesForExpandedCell(list);
        syncPreferredToInventory(segmentName, kind, list);
        setVehiclesError(null);
      } catch (err: any) {
        setVehiclesForExpandedCell([]);
        setVehiclesError(
          err?.message || 'Failed to load vehicles for this segment',
        );
      } finally {
        setVehiclesLoading(false);
      }
    },
    [dealerId, expandedCell, syncPreferredToInventory],
  );

  /**
   * Batch-fetch segment inventories for the given segment+kind pairs. Skips any that are already cached.
   * Used by the Test Template Generation confirmation step to render full vehicle info (year/make/model/etc.)
   * for selected VINs across all segments, not just the one the user expanded.
   */
  const prefetchSegmentInventories = React.useCallback(
    async (
      keys: Array<{ segmentName: string; kind: VehicleListKind }>,
    ): Promise<void> => {
      if (!dealerId || keys.length === 0) return;

      const seen = new Set<string>();
      const toFetch = keys.filter(({ segmentName, kind }) => {
        const scope = kind === 'new' ? 'NEW_VEHICLE' : 'USED_VEHICLE';
        const cacheK = cacheKey(dealerId, segmentName, scope);
        const dedupeK = `${segmentName}-${kind}`;
        if (seen.has(dedupeK)) return false;
        seen.add(dedupeK);
        return !segmentInventoriesCacheRef.current.has(cacheK);
      });

      if (toFetch.length === 0) return;

      setPrefetchLoading(true);
      try {
        await Promise.all(
          toFetch.map(async ({ segmentName, kind }) => {
            const scope = kind === 'new' ? 'NEW_VEHICLE' : 'USED_VEHICLE';
            try {
              const response =
                await EmailsGridApiService.getSegmentInventoryBySegmentAndScope(
                  dealerId,
                  segmentName,
                  scope,
                );
              const responseError = getResponseErrorMessage(response);
              if (responseError) return;

              const raw = response?.body ?? response;
              const arr = Array.isArray(raw) ? raw : [];
              const first = arr[0];
              const vehicles = first?.[scope] ?? [];
              const list = Array.isArray(vehicles) ? vehicles : [];

              const cacheK = cacheKey(dealerId, segmentName, scope);
              segmentInventoriesCacheRef.current.set(cacheK, list);
              setVehiclesBySegmentKind(prev => ({
                ...prev,
                [prefKey(segmentName, kind)]: list,
              }));
            } catch (err) {
              // Soft-fail: confirmation UI will fallback to VIN-only display for this segment+kind.
              console.warn(
                '[useSegmentInventories] prefetch failed for',
                segmentName,
                kind,
                err,
              );
            }
          }),
        );
      } finally {
        setPrefetchLoading(false);
      }
    },
    [dealerId],
  );

  /** Preferred VINs for the currently expanded cell (for UI). Persisted when user toggles. */
  const preferredVinsForExpandedCell = React.useMemo(() => {
    if (!expandedCell) return [];
    return preferredVinsByKey[prefKey(expandedCell.segmentName, expandedCell.kind)] ?? [];
  }, [expandedCell, preferredVinsByKey]);

  /** VINs in preferred but not in segment-inventory for the expanded cell (show as missing/cut in UI). */
  const missingVinsForExpandedCell = React.useMemo(() => {
    if (!expandedCell) return [];
    return missingVinsByKey[prefKey(expandedCell.segmentName, expandedCell.kind)] ?? [];
  }, [expandedCell, missingVinsByKey]);

  /** Call when user manually selects/deselects a card. Updates preferences state for future update API. */
  const togglePreferredVin = React.useCallback(
    (segmentName: string, kind: VehicleListKind, vin: string, selected: boolean) => {
      const key = prefKey(segmentName, kind);
      setPreferredVinsByKey(prev => {
        const current = prev[key] ?? [];
        const set = new Set(current);
        if (selected) set.add(vin);
        else set.delete(vin);
        return { ...prev, [key]: Array.from(set) };
      });
    },
    [],
  );

  /** Full preferred VINs state — use for future "update preferences" API. */
  const getPreferredVinsSnapshot = React.useCallback(() => ({ ...preferredVinsByKey }), [preferredVinsByKey]);

  /** Build payload for PUT from segmentRows + preferredVinsByKey and call updatePreferences. */
  const savePreferences = React.useCallback(async () => {
    const id = preferenceId;
    if (!id) throw new Error('Preference ID is required. Load preferences first.');
    setSaveLoading(true);
    try {
      const rows: SegmentRow[] = segmentRows.map(row => {
        const name = row.segment_name;
        const newVins = preferredVinsByKey[prefKey(name, 'new')] ?? [];
        const usedVins = preferredVinsByKey[prefKey(name, 'used')] ?? [];
        return {
          segment_name: name,
          new: { count: newVins.length, vins: newVins },
          used: { count: usedVins.length, vins: usedVins },
        };
      });
      const body: SegmentInventoriesSummaryResponse = {
        properties: { rows },
      };
      await EmailsGridApiService.updatePreferences(id, body);
      setInitialPreferredVinsByKey(JSON.parse(JSON.stringify(preferredVinsByKey)));
    } finally {
      setSaveLoading(false);
    }
  }, [preferenceId, segmentRows, preferredVinsByKey]);

  const hasChanges = React.useMemo(
    () => preferredVinsChanged(preferredVinsByKey, initialPreferredVinsByKey),
    [preferredVinsByKey, initialPreferredVinsByKey],
  );

  return {
    segmentRows,
    headerRow: HEADER_ROW,
    loading,
    error,
    preferenceId,
    canSavePreferences: Boolean(preferenceId) && hasChanges,
    expandedCell,
    vehiclesForExpandedCell,
    vehiclesLoading,
    vehiclesError,
    preferredVinsForExpandedCell,
    missingVinsForExpandedCell,
    togglePreferredVin,
    getPreferredVinsSnapshot,
    savePreferences,
    saveLoading,
    toggleExpanded,
    refetch: fetchSummary,
    vehiclesBySegmentKind,
    prefetchSegmentInventories,
    prefetchLoading,
  };
}
